import { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { Table } from 'src/components/base/Table';
import { LargeContainer, SimpleHeader } from 'src/components/base/Elements';
import { SubmitAction } from 'src/components/base/SubmitAction';
import { useModalDialog, ConfirmModalDialog } from 'src/components/base/ModalDialog';
import { useFetchLocation, useUpdateEffect } from 'src/utils/hooks';
import { formatFromISO } from 'src/utils/datefns';
import { useWorkspace } from 'src/model/workspaces';
import { useFetchCatches, useDeleteCatch } from 'src/model/catches';
import { useSystemStore } from 'src/stores';
import { ImageGallery } from 'src/components/base/ImageGallery';
import { fetchCatchImages } from 'src/api/api2';
import { CatchesFilterRow } from 'src/components/catches/CatchesFilterRow';
import { buildCatchesFilter, parseCatchesFilter } from 'src/utils/catches';

import classes from './Catches.module.scss';

const PAGE_SIZE = 50;
const AGES = Object.fromEntries(config.catch.ages.map(({ type, title }) => [type.toLowerCase(), title]));
const GENDERS = Object.fromEntries(config.catch.genders.map(({ type, title }) => [type.toLowerCase(), title]));

export const Catches = () => {
  const workspace = useWorkspace();
  const animals = useMemo(() => Object.fromEntries((workspace.animals ?? []).map(({ type, title }) => [type.toLowerCase(), title])), [workspace.animals]);

  const modal = useModalDialog();
  const imageGalleryModal = useModalDialog();
  const { pageIndex, sortBy, filter: filterFromUrl, setFetchLocation } = useFetchLocation();
  const { token } = useSystemStore();

  const defaultFilters = useMemo(() => ({
    trapName: '',
    type: '',
    datePeriodDays: 0,
    animal: '',
    age: '',
    sex: '',
    bait: '',
    location: '',
    photos: 'all',
    comment: '',
  }), []);

  const [filters, setFilters] = useState(() => parseCatchesFilter(filterFromUrl));
  const syncFromUrlRef = useRef(false);
  const sortByRef = useRef(sortBy);
  useEffect(() => { sortByRef.current = sortBy; }, [sortBy]);

  useEffect(() => {
    syncFromUrlRef.current = true;
    setFilters(parseCatchesFilter(filterFromUrl));
  }, [filterFromUrl]);

  useUpdateEffect(() => {
    if (syncFromUrlRef.current) {
      syncFromUrlRef.current = false;
      return;
    }

    const timeout = setTimeout(() => {
      const apiFilter = buildCatchesFilter(filters);
      setFetchLocation({ pageIndex: 0, sortBy: sortByRef.current, filter: apiFilter || undefined });
    }, 300);

    return () => clearTimeout(timeout);
  }, [filters]);

  const { data, mutate } = useFetchCatches({ pageIndex, sortBy, pageSize: PAGE_SIZE, filter: filterFromUrl });
  const [deleteCatchAction] = useDeleteCatch(mutate);

  function handleFetchData({ pageIndex, sortBy }) {
    setFetchLocation({ pageIndex, sortBy, filter: filterFromUrl });
  }

  async function handleDeleteItem({ catch_id, trap_name }) {
    const text = <span>{__('Are you sure you want to delete')} <b>{trap_name}</b>?</span>;
    if (await modal.show({ text })) {
      deleteCatchAction({ catch_id });
    }
  }

  const handleOpenPhotos = useCallback(async (catchItem) => {
    if (!catchItem?.images_count) { return; }
    const images = await fetchCatchImages({ id: catchItem.catch_id }, token);
    if (!images?.length) { return; }
    const title = `${catchItem.trap_name ?? ''} – ${catchItem.time ? formatFromISO(catchItem.time, 'P') : ''}`.trim();
    imageGalleryModal.show({ images, index: 0, title, onDownload: true });
  }, [token, imageGalleryModal]);

  const columns = useMemo(
    () => [
      {
        Header: __('Trap'),
        accessor: 'trap_name',
        className: 'fw-bold',
        style: { width: '15%' },
      },
      {
        Header: __('Type'),
        accessor: 'trap_type',
        style: { width: '10%' },
      },
      {
        Header: __('Date/Time'),
        accessor: 'time',
        Cell: ({ value }) => formatFromISO(value, 'Pp'),
        style: { width: '10%' },
      },
      {
        Header: __('Animal'),
        accessor: 'animal',
        Cell: ({ value, row: { original } }) => animals[value] ?? original?.other_animal ?? value,
        style: { width: '10%' },
      },
      {
        Header: __('Age'),
        accessor: 'age',
        Cell: ({ value }) => AGES[value] ?? value,
        style: { width: '10%' },
      },
      {
        Header: __('Sex'),
        accessor: 'sex',
        Cell: ({ value }) => GENDERS[value] ?? value,
        style: { width: '10%' },
      },
      {
        Header: __('Bait'),
        accessor: 'bait',
        style: { width: '10%' },
      },
      {
        Header: __('Location'),
        accessor: 'coordinates',
        style: { width: '10%' },
      },
      {
        Header: __('Photos'),
        accessor: 'images_count',
        disableSortBy: true,
        Cell: ({ value, row: { original } }) => {
          const count = Number(value) || 0;
          return count > 0
            ? (
              <Button className={classes.photoButton} onClick={() => handleOpenPhotos(original)} variant="outline-primary" size="sm">
                <i className="bi bi-camera" /> {count} {count === 1 ? __('photo') : __('photos')}
              </Button>
            )
            : <span className={classes.noPhoto}>{__('— no photos')}</span>;
        },
        style: { width: '10%' },
      },
      {
        Header: __('Comment'),
        accessor: 'notes',
        Cell: ({ row: { original } }) => <LastCell item={original} onDelete={handleDeleteItem} />,
        style: { width: '15%', position: 'relative' },
      },
    ],
    [animals, handleOpenPhotos]
  )

  function handleClearFilters() {
    syncFromUrlRef.current = true;
    setFilters(defaultFilters);
    setFetchLocation({ pageIndex: 0, sortBy, filter: undefined });
  }

  return (
    <LargeContainer className={classes.root}>
      <div className="mt-4 mb-5 d-flex justify-content-between align-items-baseline">
        <SimpleHeader>{__('Catches')}</SimpleHeader>
        <SubmitAction action={`${config.api.serverUrl}/exportcatches`}>{__('Export All to CSV')}</SubmitAction>
      </div>

      <div className={classes.filterBar}>
        <div className={classes.filterCount}>
          {__('Showing')} <span>{data?.items?.length ?? 0}</span> {__('of')} <span>{data?.total ?? 0}</span> {__('catches')}
        </div>
        <Button className={classes.clearFiltersButton} variant="outline-secondary" size="sm" onClick={handleClearFilters}>
          {__('Clear filters')}
        </Button>
      </div>

      <Table
        columns={columns}
        data={data.items}
        pageIndex={pageIndex}
        pageCount={data.pageCount}
        pageSize={PAGE_SIZE}
        sortBy={sortBy}
        fetchData={handleFetchData}
        renderFilterRow={() => (
          <CatchesFilterRow
            filters={filters}
            onFiltersChange={(patch) => setFilters((prev) => ({ ...prev, ...patch }))}
            typeOptions={workspace.trap_types ?? []}
            animalOptions={workspace.animals ?? []}
            ageOptions={config.catch.ages}
            sexOptions={config.catch.genders}
          />
        )}
        filterRowClassName={classes.filterRow}
      />

      <ConfirmModalDialog {...modal.register()} />
      <ImageGallery {...imageGalleryModal.register()} />
    </LargeContainer>
  )
}

const LastCell = ({ item, onDelete }) => (
  <>
    <span>{item.notes}</span>
    <Button className={classes.deleteButton} variant="danger" onClick={() => onDelete(item)}>{__('Delete')}</Button>
  </>
);